/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RefObject } from 'preact';
import { LegendItemData, LegendSectionData } from '#utils/UNSAFE_visTypes/legend';
import { Font, TextDimensions, useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { getSectionGridNums } from '#utils/PRIVATE_legendUtils/legendUtils';

type Props<K, D> = {
  width: number;
  getTextDimensions?: ((textString: string, font?: Font | undefined) => TextDimensions) | undefined;
  resolvedVars: Record<string, any>;
  rootRef: RefObject<HTMLDivElement>;
  orientation?: 'vertical' | 'horizontal';
  legendFontStylesObj?: Font;
  symbolWidth?: number;
  symbolHeight?: number;
  sections: LegendSectionData<K, D>[];
};
export type LayoutInfo = {
  totalWidth: number;
  maxItemWidth: number;
  maxItemHeight: number;
  itemsPerRow?: number;
  isWrapped?: boolean;
  isSectionWrappedArray?: boolean[];
  isLegendWrappedArray?: boolean[];
  itemsPerRowArray: number[];
};
type getTextDimensionsType = NonNullable<
  NonNullable<ReturnType<typeof useTextDimensions>>['getTextDimensions']
>;

export const getSectionalLegendLayoutInfo = <K, D extends LegendItemData<K>>({
  width,
  getTextDimensions,
  resolvedVars,
  rootRef,
  orientation,
  legendFontStylesObj = {},
  symbolWidth,
  symbolHeight,
  sections
}: Props<K, D>) => {
  let maxWidth = 0;
  let maxHeight = 0;
  let totalWidth = 0;
  const containerWidth = width;

  if (!rootRef.current || !Object.keys(resolvedVars).length || !getTextDimensions) {
    return;
  }
  const unitLength = parseFloat(resolvedVars['unit']);
  let currentRowWidth = 0;
  let maxRowWidth = 0;
  const isSectionWrappedArray: boolean[] = [];
  const isLegendWrappedArray: boolean[] = [];

  const itemsPerRowArray: number[] = [];
  let legendHeight = 0;
  const paddingTop = 0.75 * unitLength;
  // const paddingBottom = 1.5 * unitLength;

  sections.forEach((section, index) => {
    let isSectionWrapped = false;
    let isLegendWrapped = false;
    let totalSectionalWidth;
    let rowsAndCol;

    // getTextDimensions calculating text height as +6 from orignal size
    const titleDims = getTextDimensions(section.title, { fontWeight: '600' });

    //0.5 is adjustment made as getTextdimensions is not calculating correct height
    const titleHeight = titleDims.height + paddingTop + 0.5;
    const titlePaddingStart = 3;
    const titleWidth = titleDims.width + titlePaddingStart;
    const { maxItemWidth, maxItemHeight, horizontalSectionWidth } = getMaxWidth<K>(
      section.items,
      unitLength,
      getTextDimensions,
      legendFontStylesObj,
      symbolWidth,
      symbolHeight
    );
    let initialNumberOfItemsPerRow = section.items.length;
    const rowGap = 4 * unitLength;

    if (titleWidth + horizontalSectionWidth < containerWidth) {
      totalSectionalWidth = horizontalSectionWidth + titleWidth;
      if (
        currentRowWidth !== 0 &&
        currentRowWidth + rowGap + totalSectionalWidth < containerWidth &&
        !isLegendWrappedArray[index - 1]
      ) {
        currentRowWidth = currentRowWidth + totalSectionalWidth + rowGap;
      } else {
        currentRowWidth = totalSectionalWidth;
        legendHeight += Math.max(titleHeight, maxItemHeight);
        if (index !== 0) {
          legendHeight += rowGap;
        }
      }
    } else {
      isSectionWrapped = true;
      totalSectionalWidth = horizontalSectionWidth;
      const rowPadding = 0.4 * unitLength;
      if (horizontalSectionWidth > containerWidth) {
        isLegendWrapped = true;
        currentRowWidth = 0;
        initialNumberOfItemsPerRow = Math.floor(containerWidth / maxItemWidth);
        rowsAndCol = getSectionGridNums(section.items.length, initialNumberOfItemsPerRow);
        if (rowsAndCol.itemsPerCol && rowsAndCol.itemsPerRow) {
          legendHeight += titleHeight + 2 * (maxItemHeight + rowPadding) + 2 * paddingTop;
          currentRowWidth = rowsAndCol.itemsPerRow * maxItemWidth;
          if (rowsAndCol.itemsPerCol > 2) {
            //here padding gets added to top and bottom
            legendHeight += (rowsAndCol.itemsPerCol - 2) * (maxItemHeight + 2 * rowPadding);
          }
        }
      } else {
        currentRowWidth = totalSectionalWidth;
        legendHeight += maxItemHeight + titleHeight + 2 * paddingTop;
        if (index !== 0) {
          legendHeight += rowGap;
        }
      }
    }

    maxRowWidth = Math.max(currentRowWidth, maxRowWidth);
    totalWidth += horizontalSectionWidth;
    maxWidth = Math.max(maxWidth, maxItemWidth);
    maxHeight = maxItemHeight;
    isSectionWrappedArray.push(isSectionWrapped);
    isLegendWrappedArray.push(isLegendWrapped);
    itemsPerRowArray.push(orientation === 'vertical' ? 1 : Math.max(1, initialNumberOfItemsPerRow));
  });

  const legendLayoutInfo = {
    maxRowWidth: Math.ceil(maxRowWidth),
    legendHeight,
    totalWidth,
    maxItemWidth: Math.min(maxWidth, containerWidth),
    maxItemHeight: Math.ceil(maxHeight),
    itemsPerRowArray,
    isSectionWrappedArray,
    isLegendWrappedArray
  };
  return legendLayoutInfo;
};

/**
 * Returns maximum of the width among the items in a section of legend also return the width of the entire section.
 * @param items Legend items array
 * @param unitLength Base  element
 * @param getTextDimensions Text width measurement function
 * @param legendFontStylesObj Legend text font style properties object
 * @param symbolWidth Width of legend symbol
 *
 * @returns
 */
const getMaxWidth = <K>(
  items: LegendItemData<K>[],
  unitLength: number,
  getTextDimensions: getTextDimensionsType,
  legendFontStylesObj?: Font,
  symbolWidth?: number,
  symbolHeight?: number
) => {
  let maxItemWidth = 0;
  let maxItemHeight = 0;
  let horizontalSectionWidth = 0;
  let itemSymbolWidth = 0;
  let itemSymbolHeight = 0;
  const symbolPadding = unitLength * 1.5;
  if (symbolWidth) {
    itemSymbolWidth = symbolPadding + symbolWidth;
  } else {
    //below expression is simplified as, itemSymbolWidth = marginLeft + marginRight + width
    itemSymbolWidth = 4 * unitLength;
  }
  const paddingTop = 1.7 * unitLength;
  const defaultSymbolHeight = 2.5 * unitLength;
  if (symbolHeight) {
    // symbolHeight/defaultSymbolHeight + paddingTopBottom
    itemSymbolHeight = symbolHeight + paddingTop;
  } else {
    itemSymbolHeight = defaultSymbolHeight + paddingTop;
  }
  const paddingLeftRight = 2.5 * unitLength;

  items?.forEach((item) => {
    const legendItem = item as LegendItemData<K>;
    const { width, height } = getTextDimensions(legendItem.text, legendFontStylesObj);

    //itemTextWidth = paddingLeft + paddingRight + width
    const itemTextWidth = paddingLeftRight + width;
    //horizontalSectionWidth = total width of the section
    horizontalSectionWidth += itemSymbolWidth + itemTextWidth;
    maxItemWidth = Math.max(maxItemWidth, itemSymbolWidth + itemTextWidth);
    maxItemHeight = Math.max(maxItemHeight, height, itemSymbolHeight);
  });

  return { maxItemWidth, maxItemHeight, horizontalSectionWidth };
};
